import { useQuery, useMutation, useConvexAuth } from "convex/react";
import { api } from "../convex/_generated/api";
import { ScheduleMaker } from "./components/ScheduleMaker";
import { AdminDashboard } from "./components/AdminDashboard";
import { Toaster } from "@/components/ui/sonner";
import { useEffect, useRef, useState } from "react";
import { Routes, Route } from "react-router-dom";
import { Navbar } from "./components/layout/Navbar";
import { TutorialVideosDialog } from "./components/layout/Footer";
import { Icon } from "@/components/ui/icon";
import { toast } from "sonner";

import { SharePage } from "./components/SharePage";
import { PrivacyPage } from "./components/PrivacyPage";
import { TermsPage } from "./components/TermsPage";
import { usePlanArchive } from "./hooks/usePlanArchive";
import { useLocalStorage } from "./hooks/useLocalStorage";
import { useLanguage } from "./context/LanguageContext";
import { SessionProvider } from "./context/SessionContext";

function App() {
  const { t } = useLanguage();
  const { isAuthenticated } = useConvexAuth();
  const userData = useQuery(
    api.users.getCurrentUser,
    isAuthenticated ? {} : "skip",
  );
  const ensureUser = useMutation(api.users.ensureUser);
  const { pendingMigrationCount, migrateLocalPlans } = usePlanArchive();

  // Sync user to Convex
  useEffect(() => {
    if (isAuthenticated) {
      ensureUser().catch((err) => console.error("Sync user error:", err));
    }
  }, [isAuthenticated, ensureUser]);

  // Offer to import plans built before signing in. Prompted, never automatic:
  // the plans are the user's, and silently moving them is not ours to decide.
  const migrationOffered = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || pendingMigrationCount === 0) return;
    if (migrationOffered.current) return;
    migrationOffered.current = true;

    const count = pendingMigrationCount;
    toast(t("toast.migrate_title"), {
      description: t("toast.migrate_desc", { count }),
      duration: Infinity,
      action: {
        label: t("toast.migrate_action"),
        onClick: () => {
          void migrateLocalPlans()
            .then((n) => {
              if (n > 0) toast.success(t("toast.plans_imported", { count: n }));
              if (n < count) {
                toast.warning(
                  t("toast.import_partial", { imported: n, total: count }),
                );
              }
            })
            .catch((err) =>
              toast.error(t("toast.import_failed", { error: err.message })),
            );
        },
      },
    });
  }, [isAuthenticated, pendingMigrationCount, migrateLocalPlans, t]);

  // First-visit nudge toward the tutorial videos. Marked seen only when the
  // user actually dismisses it or opens the tutorial -- marking it on mount
  // (the previous approach) flipped hasSeenTutorialNudge to true one render
  // after the banner appeared, so it rendered for a single frame and vanished
  // before anyone could read it. An emptied localStorage (Hapus Sesi,
  // clearing site data, a new device) is treated as a genuinely new visit and
  // shows it again, which is fine.
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const [hasSeenTutorialNudge, setHasSeenTutorialNudge] = useLocalStorage(
    "has_seen_tutorial_nudge",
    false,
  );
  const showTutorialBanner = !hasSeenTutorialNudge;

  const [hasSeenBimcalBanner, setHasSeenBimcalBanner] = useLocalStorage(
    "has_seen_bimcal_banner",
    false,
  );
  const showBimcalBanner = !hasSeenBimcalBanner;

  return (
    <div className="h-[100dvh] flex flex-col bg-background font-sans overflow-hidden">
      <Toaster />
      <TutorialVideosDialog
        isOpen={isTutorialOpen}
        onOpenChange={setIsTutorialOpen}
      />

      <Routes>
        <Route path="/share/:shareId" element={<SharePage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route
          path="/*"
          element={
            <SessionProvider>
              <Navbar userData={userData as never} />

              {showTutorialBanner && (
                <div className="flex items-center justify-between gap-3 border-b border-warning-foreground/10 bg-warning px-4 py-2 text-caption text-warning-foreground">
                  <span className="min-w-0 truncate">
                    {t("toast.tutorial_nudge_title")}{" "}
                    {t("toast.tutorial_nudge_desc")}
                  </span>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsTutorialOpen(true);
                        setHasSeenTutorialNudge(true);
                      }}
                      className="rounded-control bg-warning-foreground/10 px-2.5 py-1 font-bold underline hover:bg-warning-foreground/20"
                    >
                      {t("toast.tutorial_nudge_action")}
                    </button>
                    <button
                      type="button"
                      aria-label="Tutup"
                      onClick={() => setHasSeenTutorialNudge(true)}
                      className="rounded-control p-1 hover:bg-warning-foreground/10"
                    >
                      <Icon name="close" size={14} />
                    </button>
                  </div>
                </div>
              )}

              {showBimcalBanner && (
                <div className="flex items-center justify-between gap-3 border-b border-primary/20 bg-primary/10 px-4 py-2 text-caption text-primary">
                  <span className="min-w-0 truncate">
                    Mau impor jadwalmu ke Google Calendar / Apple Calendar?
                  </span>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        window.open("https://bimcalendar.vercel.app", "_blank");
                        setHasSeenBimcalBanner(true);
                      }}
                      className="rounded-control bg-primary px-2.5 py-1 font-bold text-primary-foreground hover:bg-primary/90 transition-colors shadow-sm"
                    >
                      Coba Bimcalendar
                    </button>
                    <button
                      type="button"
                      aria-label="Tutup"
                      onClick={() => setHasSeenBimcalBanner(true)}
                      className="rounded-control p-1 hover:bg-primary/20 text-primary"
                    >
                      <Icon name="close" size={14} />
                    </button>
                  </div>
                </div>
              )}

              <main className="flex-1 min-h-0 overflow-hidden flex flex-col">
                <Routes>
                  <Route
                    path="/"
                    element={<ScheduleMaker userData={userData as never} />}
                  />
                  {/* AdminDashboard gates itself; no route guard needed. */}
                  <Route path="/admin" element={<AdminDashboard />} />
                </Routes>
              </main>
            </SessionProvider>
          }
        />
      </Routes>
    </div>
  );
}

export default App;
